<script lang="jsx">
import log from '@/common/log';
import plans, { AllPlans } from '@/plans';
import { ADDON_ACCORDION_THRESHOLD, ADDON_GROUP, BASE_PLAN_SUB_TYPES, planTypes } from '@/common/constants';
import { isBizPlusContext, isEduSchCmpPlusContext, isProPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { mapGetters } from 'vuex';
import { softBundleUtils } from '@/views/config/soft-bundle/helper';
import {h} from 'vue';
import { ManualChevronDownSmall, ManualChevronUpSmall } from '@zoom/icons-vue';
import CustomCarousel from '@/components/custom-carousel.vue';
import { pageConfig, flowTypes } from '@/common/service/page-config';

export const isProBuyFlow = () =>
  pageConfig.plan === BASE_PLAN_SUB_TYPES.PRO &&
  [flowTypes.buyNow, flowTypes.basic2pro].includes(pageConfig.type);

export const simpleFilterAddonForKeys = (list) => {
  return list
    .filter((x) => { return x })
    .sort((a, b) => {
      // If a is recommended and b is not, a comes first
      if (!!a.components?.addon?.recommended && !b.components?.addon?.recommended) return -1;
      // If b is recommended and a is not, b comes first
      if (!a.components?.addon?.recommended && !!b.components?.addon?.recommended) return 1;
      // If both are recommended or both are not, keep original order
      return 0;
    })
};

export const filterAddonForKeys = (...args) => {
  const [list, filterForKeys = []] = args
  return list
  .filter((x) => {
    if (!x) return false;
    // if(!!store.getters.ghostBasePlanKey && store.getters.ghostBasePlanKey == x.key) return true; // ghost plan counterpart should also be listed
    if (!filterForKeys.length) return true;
    const For = x.components?.addon?.for;
    if (For === '*') return true;
    if (Array.isArray(For)) {
      if (filterForKeys.some((y) => For.includes(y))) return true;
    }
    if (typeof For === 'function') {
      const ForPlanList = For(args);
      if (!Array.isArray(ForPlanList)) return !!ForPlanList
      if (filterForKeys.some((y) => ForPlanList.includes(y))) return true;
    }
    return false;
  })
  .sort((a, b) => {
    // If a is recommended and b is not, a comes first
    if (!!a.components?.addon?.recommended && !b.components?.addon?.recommended) return -1;
    // If b is recommended and a is not, b comes first
    if (!a.components?.addon?.recommended && !!b.components?.addon?.recommended) return 1;
    // If both are recommended or both are not, keep original order
    return 0;
  })
};

export const planTypesForMeeting = () => {
  return planTypes.map([planTypes.ZOOM_MEETING, planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS, planTypes.ZONE_EDU_PLUS]);
};

export default {
  components: {
    CustomCarousel,
  },
  props: {
    group: {
      type: String,
      default: undefined,
    },
    filter: {
      type: Function,
      default: list => list,
    },
    filterKeys: {
      type: Array,
      default: () => [],
    },
    defaultShowNum:{
      type: Number,
      default: ADDON_ACCORDION_THRESHOLD,
    }
  },
  data() {
    return {
      isAccordionExpanded: false,
    };
  },
  computed: {
    ...mapGetters(['pastPlans']),
  },
  methods: {
    renderAccordionButton() {
      const toggle = () => {
        this.isAccordionExpanded = !this.isAccordionExpanded;
        this.$nextTick(()=>{
          if(this.isAccordionExpanded){
            const addonDoms = [...document.querySelectorAll(`.opc-tile.opc-addon`)];
            // focus on first expanded addon
            const expanded0 = addonDoms[this.defaultShowNum];
            if(!expanded0) return;
            return expanded0?.querySelector('.opc-addon__action .zbo-button')?.focus();
          }else{
            return document.querySelector('.opc-cfgcore__addon__toggle')?.focus();
          }
        });
      };
      const button = (text, icon) => (
        <zm-button data-testid="checkout-button-380" tabindex="0" aria-expanded={this.isAccordionExpanded ? 'true' : 'false'} type="link" class="opc-cfgcore__addon__toggle" onClick={toggle} icon={icon} icon-reverse>
          {text}
        </zm-button>
      );
      if (this.isAccordionExpanded) return button(this.$t('billing.addon_toggle_less'), ManualChevronUpSmall);
      return button(this.$t('billing.addon_toggle_more'), ManualChevronDownSmall);
    },
    renderAddonsWithAccordion(addons) {
      const threshold = this.defaultShowNum;
      if (addons.length <= threshold) return addons;
      return [
        ...addons.slice(0, this.isAccordionExpanded ? addons.length + 1 : threshold),
        this.renderAccordionButton(),
      ];
    },
    renderCarouselAddons(addons) {
      let switchCount = 3;
      const { modal, mobile } = this.$context.is;
      if (modal) {
        switchCount = 2;
      }
      if (mobile) {
        switchCount = 1;
      }
      return [
        (
          <CustomCarousel class="opc-cfgcore__addon-carousel" items={addons} slide-width={280} slide-gap={16} switch-count={switchCount}>
            {({ item: addon }) => {
              return h('div', { style: { height: '100%' }, class: 'opc-addon-carousel-item' }, [addon])
            }}
          </CustomCarousel>
        )
      ];
    },
    ABTest(addons) {
      if (window.$isEnabled && window.$isEnabled('enableCarouselAddonList') && this.group === ADDON_GROUP.DEFAULT) {
        return this.renderCarouselAddons(addons);
      }
      if (isBizPlusContext || isProPlusContext || this.$context.is.edu || softBundleUtils.isSimilarPbxBundle()) return this.renderAddonsWithAccordion(addons);
      if (isEduSchCmpPlusContext) {
        return this.pastPlans.length > 0 ? this.renderAddonsWithAccordion(addons) : addons;
      }
      if(this.$context.type !== planTypes.ZOOM_MEETING){
        const lessRecommendPlans = addons.slice(0, 4);
        return this.pastPlans.length > 0 ? this.renderAddonsWithAccordion(lessRecommendPlans) : lessRecommendPlans;
      }
      return this.renderAddonsWithAccordion(addons);
    },
  },
  render() {
    const list = (this.group === ADDON_GROUP.WIN_BACK ? AllPlans : plans).filter((x) => {
      if (!x.components?.addon) return false;
      return true;
    });
    const addons = this.filterKeys.map((key) => list.find((x) => x.key === key)).filter(x => {
      if (this.group === ADDON_GROUP.WIN_BACK) {
        return true;
      }
      const _group = x.components?.addon?.group;

      const group = typeof _group === 'function' ? _group() : _group;

      if (Array.isArray(group)) {
        return group.includes(this.group);
      }
      return this.group === group;
    });
    const filter = window.__hook_addons_filter__ || this.filter;

    log('config:addons:list', this.filterKeys, this.group);
    let filtered = filter(addons);
    log('config:addons', filtered);
    if(!filtered.length) return;
    const addonGroup = this.group;
    return h(
      'div',
      {},
      [
        this.$slots.default?.(),
        ...this.ABTest(
        filtered.map((x) =>
          h(x.components.addon, {
            group: addonGroup,
            onAdd: ($data) => {
              this.$emit('add', x.key, $data, x);
            },
          }),
        ),
      )]
    );
  },
};
</script>

<style lang="scss">
.opc-cfgcore__addon {
  &__toggle {
    margin-top: 10px;
  }
}

/**
 * https://zoomvideo.atlassian.net/browse/OBT-6928
 * AB Test: Enable Carousel Addon List
 */
.opc-cfgcore__addon-enhance--carousel {
  &.is-teleported {
    padding-bottom: 80px;
  }

  .opc-cfgcore__addon-carousel {
    .opc-addon {
      height: calc(100% - 28px);
      margin-top: 28px;
      padding: 16px;
      padding-bottom: calc(16px + 32px + 8px) !important;

      &.opc-addon__has-promo {
        position: relative;
        border-top-left-radius: 0;
        border-top-right-radius: 0;

        .opc-addon__banner-promo {
          text-transform: lowercase;
          &::first-letter {
            text-transform: uppercase;
          }
          position: absolute;
          top: -28px;;
          left: -1px;
          right: -1px;

          color: #FFFFFF;
          font-weight: 600;
          font-size: 16px;
          line-height: 28px;
          text-align: center;
          background-color: var(--zoom-color-fill-global-complementary);
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
        }

        &.opc-addon__free-trial {
          .opc-addon__banner-promo {
            background-color: var(--zoom-color-fill-global-supplementary1);
          }
        }
      }

      .opc-addon__head {
        margin-right: 0px !important;

        .opc-addon__logo {
          height: 30px;
          width: 30px;
          .plan-icon {
            height: 30px;
            width: 30px;
          }
        }

        .opc-addon__title {
          & > .zbo-badge {
            display: none;
          }
        }

        .opc-addon__subtitle {
          font-size: 12px;
          line-height: 16px;
        }
      }

      .opc-addon__description {
        margin-right: 0 !important;
        padding-left: 0px;
      }
  
      .opc-addon__action {
        position: absolute;
        top: unset;
        bottom: -40px;

        .opc-addon__action-sub {
          display: inline-block;
          margin-left: 16px;
        }
      }
    }
  }
}
</style>
